'use client';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsLogoutPopupShow,
  setIsOpenLoginSignup,
  setIsOpenLoginSuccessModal,
} from '@/redux/reducers/auth-reducer';
import { updateCheckoutPopupState } from '@/redux/reducers/global-popups-reducer';
import useTranslation from '@/hooks/useTranslation';
import {
  resetCheckoutSummaryForCache,
  resetCheckoutSummaryMessage,
} from '@/redux/reducers/checkout-summary-reducer';
import { resetCartForCache } from '@/redux/reducers/carts-reducer';
import { resetProductComparisonForCache } from '@/redux/reducers/product-comparison-reducer';
import { resetMyProfileForCache } from '@/redux/reducers/my-profile-reducer';
import { resetProductForCache } from '@/redux/reducers/products-reducer';
import { resetShippingAddressForCache } from '@/redux/reducers/shipping-address-reducer';
import { resetOrdersForCache } from '@/redux/reducers/orders-reducer';
import { resetCategoriesForCache } from '@/redux/reducers/categories-reducer';
import { resetPlaceOrderForCache } from '@/redux/reducers/place-order-reducer';
import { fetchCartItemsAsync } from '@/redux/thunk/carts-thunk';
import { usePathname } from 'next/navigation';
import { resetSpinTheWheelForCache } from '@/redux/reducers/spin-the-wheel-reducer';
import { getUser } from '@/redux/thunk/auth-thunk';
import { fetchViewedProducts } from '@/redux/thunk/recently-viewed-thunk';
import dynamic from 'next/dynamic';
import Navigation from './components/Navigation';
import LoginSignup from './components/Auth/LoginSignup';
import ChatComponent from './_chat/Components/ChatComponent';
import ScrollToTop from './components/Common/ScrollTop';
import SpinTheWheelInitializer from './components/Modal/SpinTheWheel/SpinTheWheelInitializer';
import AppHeader from './components/Navigation/AppHeader';

const CheckoutSnackBar = dynamic(
  () => import('./components/SnackBar/CheckoutSnackBar'),
  { ssr: false },
);
const CommonSnackBar = dynamic(
  () => import('./components/SnackBar/CommonSnackBar'),
  { ssr: false },
);
const AllShippingAddressPopups = dynamic(
  () => import('./components/Popups/ShippingAddress/AllShippingAddressPopups'),
  { ssr: false },
);
const AllPopups = dynamic(() => import('./components/Popups/AllPopups'), {
  ssr: false,
});
const Modal = dynamic(() => import('./components/Modal'), { ssr: false });

function DefaultLayout({ children }) {
  const dispatch = useDispatch();
  const t = useTranslation('auth');
  const pathName = usePathname();
  const {
    user,
    isLogoutPopupShow,
    isOpenLoginSuccessModal,
    isOpenLoginSignup,
    isLoggedIn,
  } = useSelector((state) => state.auth);
  const { checkoutPopup } = useSelector((state) => state.popups);
  const { message: checkoutSummaryMessage } = useSelector(
    (state) => state.checkoutSummary,
  );
  const [showChatIcon, setShowChatIcon] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  const handleCloseLoginSignup = () => {
    dispatch(setIsOpenLoginSignup(false));
  };

  async function checkInternet() {
    try {
      await fetch('https://singerbd.com/', { mode: 'no-cors' });
      return true; // If fetch succeeds, internet is available
    } catch (error) {
      return false; // If request fails, no network
    }
  }

  const resetAllStates = () => {
    dispatch(resetCartForCache());
    dispatch(resetProductComparisonForCache());
    dispatch(resetMyProfileForCache());
    dispatch(resetProductForCache());
    dispatch(resetShippingAddressForCache());
    dispatch(resetOrdersForCache());
    dispatch(resetCategoriesForCache());
    dispatch(resetCheckoutSummaryForCache());
    dispatch(resetPlaceOrderForCache());
    dispatch(resetSpinTheWheelForCache());
  };

  useEffect(() => {
    const version = localStorage.getItem('version');
    if (process.env.NEXT_PUBLIC_APP_VERSION !== version) {
      console.log(
        'A newer version of the application is available. Page will reload automatically.',
      );
      resetAllStates();
      localStorage.setItem('version', process.env.NEXT_PUBLIC_APP_VERSION);
      dispatch(fetchCartItemsAsync());
    }
  }, [window]);

  // For Checking Internet connection
  // useEffect(() => {
  //   const verifyNetwork = async () => {
  //     const online = await checkInternet();
  //     setIsOnline(online);
  //   };

  //   verifyNetwork();

  //   // Optionally, check periodically every 5 seconds
  //   const interval = setInterval(verifyNetwork, 5000);

  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    if (
      pathName === '/' ||
      pathName.startsWith('/category') ||
      pathName.startsWith('/product') ||
      pathName.startsWith('/brand') ||
      pathName.startsWith('/my-singer')
    ) {
      setShowChatIcon(true);
    } else {
      setShowChatIcon(false);
    }
  }, [pathName]);

  useEffect(() => {
    setTimeout(() => {
      if (isLoggedIn) {
        dispatch(fetchCartItemsAsync());
      }
    }, 2000);
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && (user === null || user === undefined)) {
      dispatch(getUser());
    }

    if (isLoggedIn) {
      dispatch(fetchViewedProducts());
    }
  }, [isLoggedIn, user]);

  return (
    <>
      <AppHeader isOnline={false} />

      <main className="w-full min-h-screen">{children}</main>

      {/* use to scroll top always */}
      <ScrollToTop />

      {/* use to track when user visit */}
      <SpinTheWheelInitializer />

      {isLogoutPopupShow ? (
        <Modal
          type="logout-successful-modal"
          data={t('auth:logoutSuccessful')}
          onView={isLogoutPopupShow}
          onClose={setIsLogoutPopupShow}
        />
      ) : null}

      {isOpenLoginSuccessModal ? (
        <Modal
          type="login-successful-modal"
          data={t('auth:loginSuccessful')}
          onView={isOpenLoginSuccessModal}
          onClose={setIsOpenLoginSuccessModal}
        />
      ) : null}

      {/* TODO: updated for checkout and payment */}
      {checkoutPopup?.isShow ? (
        <CommonSnackBar
          title={checkoutPopup.message}
          isSuccess={checkoutPopup.isSuccess}
          onView={checkoutPopup.isShow}
          className="bottom-[-130px] lg:bottom-[-80px]"
          onClose={() =>
            dispatch(
              updateCheckoutPopupState({
                isShow: false,
                isSuccess: false,
                message: '',
              }),
            )
          }
        />
      ) : null}

      <AllShippingAddressPopups />

      {/* for checkout summary */}
      {checkoutSummaryMessage?.isShow ? (
        <CheckoutSnackBar
          title={checkoutSummaryMessage.text}
          isSuccess={checkoutSummaryMessage.isSuccess}
          onView={checkoutSummaryMessage.isShow}
          className="bottom-[-130px] lg:bottom-[-80px]"
          onClose={() =>
            dispatch(
              resetCheckoutSummaryMessage({
                isShow: false,
                isSuccess: false,
                message: '',
              }),
            )
          }
        />
      ) : null}

      {isOpenLoginSignup ? (
        <LoginSignup
          onView={isOpenLoginSignup}
          onClose={handleCloseLoginSignup}
        />
      ) : null}

      <AllPopups />

      <ChatComponent floatChatIcon={showChatIcon} />

      <Navigation type="footer-primary" />
    </>
  );
}

export default DefaultLayout;
